import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, Lock } from 'lucide-react'
import { useCartStore } from '../store/cartStore'
import axios from 'axios'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api'

const CheckoutPage: React.FC = () => {
  const { items, summary, fetchCart } = useCartStore()
  const [isProcessing, setIsProcessing] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    fetchCart()
  }, [fetchCart])

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart')
    }
  }, [items, navigate])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(price)
  }

  const handlePayment = async () => {
    setIsProcessing(true)
    try {
      // Create order
      const orderResponse = await axios.post(`${API_BASE_URL}/orders/create`)
      const { orderId, orderNumber } = orderResponse.data

      // Create payment token
      const paymentResponse = await axios.post(`${API_BASE_URL}/payment/create-token`, {
        orderId
      })
      const { token } = paymentResponse.data

      // Redirect to Midtrans payment page or use Snap
      if (window.snap) {
        window.snap.pay(token, {
          onSuccess: (result: any) => {
            toast.success('Pembayaran berhasil!')
            navigate(`/payment/success?order_id=${orderNumber}`)
          },
          onPending: (result: any) => {
            toast.info('Pembayaran pending, silakan selesaikan pembayaran')
            navigate(`/payment/pending?order_id=${orderNumber}`)
          },
          onError: (result: any) => {
            toast.error('Pembayaran gagal')
            navigate(`/payment/error?order_id=${orderNumber}`)
          },
          onClose: () => {
            toast.info('Pembayaran dibatalkan')
          }
        })
      } else {
        toast.error('Payment gateway tidak tersedia')
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Terjadi kesalahan'
      toast.error(message)
    } finally {
      setIsProcessing(false)
    }
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Ringkasan Pesanan
              </h2>

              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4">
                    <img
                      src={item.product.thumbnailUrl || '/placeholder-product.jpg'}
                      alt={item.product.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {item.product.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {formatPrice(item.itemTotal)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <hr className="my-6" />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(summary.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Biaya Admin</span>
                  <span className="font-medium">Gratis</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-blue-600">{formatPrice(summary.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Metode Pembayaran
              </h2>

              <div className="space-y-4 mb-6">
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Midtrans Payment Gateway
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Kartu Kredit, Debit, Bank Transfer, E-Wallet
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Lock className="h-5 w-5 text-blue-600" />
                  <span className="text-blue-800 font-medium">Pembayaran Aman</span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  Transaksi Anda dilindungi dengan enkripsi SSL 256-bit
                </p>
              </div>

              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Memproses...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    Bayar Sekarang {formatPrice(summary.total)}
                  </>
                )}
              </button>

              <p className="text-center text-gray-600 text-sm mt-4">
                Dengan melanjutkan, Anda menyetujui{' '}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  Syarat & Ketentuan
                </a>{' '}
                kami
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CheckoutPage
